import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { StyleSheet, View, Platform, Animated, Dimensions } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import { useUserBanners, isCoolingDown } from '@/hooks/useUserBanners';
import { pickActiveGlobalPlacement } from '@/lib/bannerPlacementLogic';
import { getAnchorRef, subscribeAnchors } from '@/hooks/useTooltipAnchor';
import { trackEvent } from '@/constants/utils/analytics';
import { useRouter } from 'expo-router';
import { useModalStore, useUIStore } from '@/store';
import useResponsiveness from '@/hooks/useResponsiveness';
import { devLog } from '@/lib/devLog';
import { executeBannerCta } from '@/lib/bannerActions';
import TooltipCardBody, { ARROW_SIZE, getTooltipAccent, getTooltipCardBg } from './TooltipCardBody';

const SIDEBAR_ANCHORS = new Set([
  'new-chat', 'files-nav', 'memory-nav', 'tasks-nav',
  'models-nav', 'agents-nav', 'images-nav', 'sidebar-pro',
]);

const TOOLTIP_WIDTH = 280;
const VIEWPORT_PADDING = 12;

function measureAnchor(ref, anchorId) {
  const el = ref?.current;
  if (!el) {
    devLog('[TooltipMeasure]', anchorId, '→ ref.current is null');
    return null;
  }

  if (Platform.OS === 'web') {
    if (typeof el.getBoundingClientRect === 'function') {
      const rect = el.getBoundingClientRect();
      return { x: rect.left, y: rect.top, width: rect.width, height: rect.height };
    }
    devLog('[TooltipMeasure]', anchorId, '→ no getBoundingClientRect, el type:', typeof el, Object.keys(el).slice(0, 5));
    return null;
  }

  return new Promise((resolve) => {
    if (typeof el.measureInWindow === 'function') {
      el.measureInWindow((x, y, width, height) => {
        resolve({ x, y, width, height });
      });
    } else if (typeof el.measure === 'function') {
      el.measure((x, y, width, height, pageX, pageY) => {
        resolve({ x: pageX, y: pageY, width, height });
      });
    } else {
      resolve(null);
    }
  });
}

function TooltipCard({ banner, anchorRect, onDismiss, onCTA, colors, isDark, theme }) {
  const accentColor = getTooltipAccent(banner.contentType, colors);

  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(0)).current;
  const hasFiredSeen = useRef(false);
  const [measuredHeight, setMeasuredHeight] = useState(150);
  const { markSeen } = useUserBanners();

  const windowHeight = Dimensions.get('window').height;
  const windowWidth = Dimensions.get('window').width;
  const spaceAbove = anchorRect.y;
  const spaceBelow = windowHeight - (anchorRect.y + anchorRect.height);
  const showAbove = spaceAbove > spaceBelow;

  const handleLayout = useCallback((e) => {
    const { height } = e.nativeEvent.layout;
    if (height > 0) setMeasuredHeight(height);
  }, []);

  useEffect(() => {
    slideAnim.setValue(showAbove ? 8 : -8);
    if (!hasFiredSeen.current) {
      hasFiredSeen.current = true;
      markSeen(banner.id);
      Animated.parallel([
        Animated.timing(fadeAnim, { toValue: 1, duration: 350, useNativeDriver: true }),
        Animated.spring(slideAnim, { toValue: 0, tension: 50, friction: 9, useNativeDriver: true }),
      ]).start();
    }
  }, []);

  const handleDismiss = useCallback(() => {
    trackEvent('engagement', 'banner_dismissed', 'tooltip', banner.id);
    Animated.parallel([
      Animated.timing(fadeAnim, { toValue: 0, duration: 200, useNativeDriver: true }),
      Animated.timing(slideAnim, { toValue: showAbove ? 8 : -8, duration: 200, useNativeDriver: true }),
    ]).start(() => onDismiss(banner.id));
  }, [banner.id, onDismiss, fadeAnim, slideAnim, showAbove]);

  const handleCTA = useCallback(() => {
    trackEvent('conversion', 'banner_cta_clicked', 'tooltip', banner.id);
    onCTA(banner);
  }, [banner, onCTA]);

  const anchorCenterX = anchorRect.x + anchorRect.width / 2;

  let tooltipLeft = anchorCenterX - TOOLTIP_WIDTH / 2;
  tooltipLeft = Math.max(VIEWPORT_PADDING, Math.min(tooltipLeft, windowWidth - TOOLTIP_WIDTH - VIEWPORT_PADDING));

  const arrowLeft = Math.max(16, Math.min(anchorCenterX - tooltipLeft - ARROW_SIZE, TOOLTIP_WIDTH - 32));

  const tooltipTop = showAbove
    ? anchorRect.y - measuredHeight - ARROW_SIZE - 5
    : anchorRect.y + anchorRect.height + ARROW_SIZE + 5;

  const cardBg = getTooltipCardBg(colors);
  const { content, isDismissible } = banner;

  const styles = useMemo(() => StyleSheet.create({
    wrapper: {
      position: Platform.OS === 'web' ? 'fixed' : 'absolute',
      top: tooltipTop,
      left: tooltipLeft,
      width: TOOLTIP_WIDTH,
      zIndex: 9990,
    },
    arrow: {
      position: 'absolute',
      left: arrowLeft,
      width: 0,
      height: 0,
      borderLeftWidth: ARROW_SIZE,
      borderLeftColor: 'transparent',
      borderRightWidth: ARROW_SIZE,
      borderRightColor: 'transparent',
      zIndex: 1,
      ...(showAbove
        ? { bottom: -(ARROW_SIZE - 1), borderTopWidth: ARROW_SIZE, borderTopColor: cardBg }
        : { top: -(ARROW_SIZE - 1), borderBottomWidth: ARROW_SIZE, borderBottomColor: cardBg }),
    },
  }), [tooltipTop, tooltipLeft, arrowLeft, showAbove, cardBg]);

  return (
    <Animated.View
      onLayout={handleLayout}
      style={[
        styles.wrapper,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
    >
      {!showAbove && <View style={styles.arrow} />}
      <TooltipCardBody
        content={content}
        contentType={banner.contentType}
        isDismissible={isDismissible}
        accentColor={accentColor}
        colors={colors}
        isDark={isDark}
        theme={theme}
        onDismiss={handleDismiss}
        onCTA={handleCTA}
        compact
      />
      {showAbove && <View style={styles.arrow} />}
    </Animated.View>
  );
}

function TooltipBannerLayer() {
  const { colors, theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const { banners, dismiss, markClicked, getBannersForPlacement } = useUserBanners();
  const router = useRouter();
  const { openModal } = useModalStore(state => state.actions);
  const isModalOpen = useModalStore(state => state.isOpen);
  const hasModalBanners = getBannersForPlacement('modal').length > 0;
  const isSidebarVisible = useUIStore(state => state.isSidebarVisible);
  const { isXS } = useResponsiveness();

  const [anchorVersion, setAnchorVersion] = useState(0);
  const [measurements, setMeasurements] = useState({});
  const [dismissed, setDismissed] = useState(new Set());

  useEffect(() => {
    return subscribeAnchors(() => setAnchorVersion(v => v + 1));
  }, []);

  const activeGlobalPlacement = useMemo(
    () => pickActiveGlobalPlacement(banners, isCoolingDown),
    [banners],
  );

  const tooltipBanners = useMemo(() => {
    if (activeGlobalPlacement !== 'tooltip') return [];
    return banners
      .filter(b => b.placement === 'tooltip' && b.tooltipAnchor)
      .sort((a, b) => (b.priority || 0) - (a.priority || 0));
  }, [banners, activeGlobalPlacement]);

  useEffect(() => {
    if (!tooltipBanners.length) return;

    let cancelled = false;

    const measure = async () => {
      const results = {};
      for (const b of tooltipBanners) {
        const ref = getAnchorRef(b.tooltipAnchor);
        if (!ref) {
          devLog('[TooltipMeasure]', b.tooltipAnchor, '→ no ref registered');
          continue;
        }
        const rect = await measureAnchor(ref, b.tooltipAnchor);
        if (rect && rect.width > 0) {
          results[b.tooltipAnchor] = rect;
          devLog('[TooltipMeasure]', b.tooltipAnchor, '→', Math.round(rect.x), Math.round(rect.y), Math.round(rect.width), 'x', Math.round(rect.height));
        }
      }
      if (!cancelled) setMeasurements(results);
    };

    const timer = setTimeout(measure, 500);

    let interval;
    if (Platform.OS === 'web') {
      interval = setInterval(measure, 3000);
    }

    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (interval) clearInterval(interval);
    };
  }, [tooltipBanners, anchorVersion]);

  const handleDismiss = useCallback((bannerId) => {
    setDismissed(prev => new Set([...prev, bannerId]));
    dismiss(bannerId);
  }, [dismiss]);

  const handleCTA = useCallback((banner) => {
    markClicked(banner.id);
    executeBannerCta(banner, { router, openModal });
    handleDismiss(banner.id);
  }, [router, openModal, markClicked, handleDismiss]);

  const activeBanner = tooltipBanners.find(b => {
    if (dismissed.has(b.id)) return false;
    if (!measurements[b.tooltipAnchor]) return false;
    if (isCoolingDown(`tooltip:${b.tooltipAnchor}`)) return false;
    if (isCoolingDown('_post_modal')) return false;
    if (isXS && !isSidebarVisible && SIDEBAR_ANCHORS.has(b.tooltipAnchor)) return false;
    return true;
  });

  devLog('[TooltipBannerLayer]', activeBanner ? `showing: ${activeBanner.content?.headline}` : 'none', `| ${tooltipBanners.length} tooltip banners, ${Object.keys(measurements).length} anchors measured`);

  if (!activeBanner) return null;
  if (isModalOpen || hasModalBanners) return null;

  return (
    <TooltipCard
      key={activeBanner.id}
      banner={activeBanner}
      anchorRect={measurements[activeBanner.tooltipAnchor]}
      onDismiss={handleDismiss}
      onCTA={handleCTA}
      colors={colors}
      isDark={isDark}
      theme={theme}
    />
  );
}

export default React.memo(TooltipBannerLayer);
